import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const STATUS_STYLE = {
  active:    { bg: C.successBg, color: C.successText, label: 'Active'    },
  completed: { bg: C.infoBg,    color: C.infoText,    label: 'Completed' },
  cancelled: { bg: C.dangerBg,  color: C.dangerText,  label: 'Cancelled' },
};

const BLANK_FORM = {
  client_id: '', installer_id: '', package_id: '',
  total_amount: '', total_amc: '', notes: '',
  application_id: '', ref_number: '',
};

export default function ProductMapping() {
  const [mappings,   setMappings]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);   // 'create' | 'view' | 'edit' | 'delete'
  const [selMap,     setSelMap]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState(null);
  const [search,     setSearch]     = useState('');

  // Installer mapping requests (Requests tab)
  const [tab,       setTab]       = useState('mappings');   // 'mappings' | 'requests'
  const [requests,  setRequests]  = useState([]);
  const [reqTab,    setReqTab]    = useState('pending');
  const [act,       setAct]       = useState(null);         // { req, mode:'approve'|'reject' }
  const [reqAmount, setReqAmount] = useState('');
  const [reqAmc,    setReqAmc]    = useState('');
  const [reqNote,   setReqNote]   = useState('');
  const [reqBusy,   setReqBusy]   = useState(false);
  const [reqErr,    setReqErr]    = useState('');

  // Dropdown options
  const [clients,      setClients]      = useState([]);
  const [installers,   setInstallers]   = useState([]);
  const [applications, setApplications] = useState([]);
  const [instPkgs,     setInstPkgs]     = useState([]);
  const [pkgItems,     setPkgItems]     = useState([]);

  // Client searchable combobox state
  const [clientSearch,   setClientSearch]   = useState('');
  const [clientDropOpen, setClientDropOpen] = useState(false);
  const clientDropRef = useRef(null);

  // Form state
  const [form,   setForm]   = useState(BLANK_FORM);
  const [fErr,   setFErr]   = useState('');
  const [loadingPkgs,    setLoadingPkgs]    = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/product-mappings'); setMappings(r.data.filter(m => m.entry_type !== 'service')); }
    catch {}
    finally { setLoading(false); }
  }, []);

  const loadRequests = useCallback(async () => {
    try { const r = await api.get('/api/mapping-requests'); setRequests(r.data); } catch {}
  }, []);

  useEffect(() => { loadAll(); loadRequests(); }, [loadAll, loadRequests]);

  const openAct = (req, mode) => {
    setAct({ req, mode });
    setReqAmount(mode === 'approve' ? String(req.suggested_total || '') : '');
    setReqAmc(mode === 'approve' ? String(req.suggested_amc || '') : '');
    setReqNote(''); setReqErr('');
  };

  const submitAct = async () => {
    if (!act) return;
    const { req, mode } = act;
    if (mode === 'approve' && !(Number(reqAmount) > 0)) { setReqErr('Enter the cost to charge the client.'); return; }
    setReqBusy(true); setReqErr('');
    try {
      if (mode === 'approve') {
        await api.post(`/api/mapping-requests/${req.id}/approve`, {
          total_amount: Number(reqAmount), total_amc: Number(reqAmc) || 0,
          admin_notes: reqNote.trim() || null,
        });
        showToast('success', `${req.request_id} confirmed — mapping created.`);
      } else {
        await api.post(`/api/mapping-requests/${req.id}/reject`, { admin_notes: reqNote.trim() || null });
        showToast('success', `${req.request_id} rejected.`);
      }
      setAct(null);
      await Promise.all([loadRequests(), loadAll()]);
    } catch (e) {
      setReqErr(e?.response?.data?.detail || 'Action failed.');
    } finally { setReqBusy(false); }
  };

  const reqCounts = {
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  // Outside-click closes client dropdown
  useEffect(() => {
    if (!clientDropOpen) return;
    const handler = (e) => {
      if (clientDropRef.current && !clientDropRef.current.contains(e.target)) {
        setClientDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clientDropOpen]);

  // ── Open create ──────────────────────────────────────────────────────────────

  const openCreate = async () => {
    setForm(BLANK_FORM); setFErr('');
    setInstPkgs([]); setPkgItems([]);
    setClientSearch(''); setClientDropOpen(false);
    setSaving(false);
    try {
      const [cl, ins, apps] = await Promise.all([
        api.get('/api/clients'),
        api.get('/api/users/installers'),
        api.get('/api/applications'),
      ]);
      setClients(cl.data.filter(c => c.status === 'active' || !c.status));
      setInstallers(ins.data);
      setApplications(apps.data.filter(a => a.status === 'active' || !a.status));
    } catch { setClients([]); setInstallers([]); setApplications([]); }
    setModal('create');
  };

  // Client combobox helpers
  const filteredClients = clients.filter(c => {
    if (!clientSearch.trim()) return true;
    const q = clientSearch.toLowerCase();
    return (
      c.client_name?.toLowerCase().includes(q) ||
      c.client_id?.toLowerCase().includes(q) ||
      c.company_name?.toLowerCase().includes(q)
    );
  });

  const selectClient = (c) => {
    setForm(f => ({ ...f, client_id: c.id }));
    setClientSearch(c.client_name);
    setClientDropOpen(false);
  };

  const selectedClientObj = clients.find(c => c.id === Number(form.client_id));

  // When installer changes → load their current packages
  const onInstallerChange = async (installerId) => {
    setForm(f => ({ ...f, installer_id: installerId, package_id: '' }));
    setPkgItems([]); setInstPkgs([]);
    if (!installerId) return;
    setLoadingPkgs(true);
    try {
      const r = await api.get(`/api/installer/${installerId}/current-packages`);
      setInstPkgs(r.data);
    } catch { setInstPkgs([]); }
    finally { setLoadingPkgs(false); }
  };

  // When package changes → fetch price summary
  const onPackageChange = async (packageId) => {
    setForm(f => ({ ...f, package_id: packageId, total_amount: '', total_amc: '' }));
    setPkgItems([]);
    if (!packageId) return;
    setLoadingSummary(true);
    try {
      const r = await api.get(`/api/packages/${packageId}/price-summary`);
      setForm(f => ({
        ...f,
        total_amount: r.data.total_amount,
        total_amc:    r.data.total_amc,
      }));
      setPkgItems(r.data.items || []);
    } catch { setPkgItems([]); }
    finally { setLoadingSummary(false); }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const saveMapping = async () => {
    if (!form.client_id)    return setFErr('Please select a client.');
    if (!form.installer_id) return setFErr('Please select an installer.');
    if (!form.package_id)   return setFErr('Please select a package.');
    setFErr(''); setSaving(true);
    try {
      await api.post('/api/product-mappings', {
        client_id:      Number(form.client_id),
        installer_id:   Number(form.installer_id),
        package_id:     Number(form.package_id),
        total_amount:   Number(form.total_amount) || 0,
        total_amc:      Number(form.total_amc)    || 0,
        notes:          form.notes.trim() || null,
        application_id: form.application_id ? Number(form.application_id) : null,
        ref_number:     form.ref_number.trim() || null,
      });
      await loadAll();
      showToast('success', 'Product mapping created.');
      setModal(null);
    } catch (e) {
      setFErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // ── Edit / Delete ─────────────────────────────────────────────────────────────

  const openEdit = (m) => { setSelMap(m); setModal('edit'); setSaving(false); setFErr(''); };

  const saveEdit = async () => {
    setSaving(true); setFErr('');
    try {
      await api.put(`/api/product-mappings/${selMap.id}`, {
        total_amount: Number(selMap.total_amount) || 0,
        total_amc:    Number(selMap.total_amc)    || 0,
        notes:        selMap.notes || null,
        status:       selMap.status,
        ref_number:   selMap.ref_number?.trim() || null,
      });
      await loadAll();
      showToast('success', 'Mapping updated.');
      setModal(null);
    } catch (e) {
      setFErr(e?.response?.data?.detail || 'Update failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/product-mappings/${selMap.id}`);
      await loadAll();
      showToast('success', `${selMap.mapping_id} deleted.`);
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  // ── Filter ────────────────────────────────────────────────────────────────────

  const filtered = mappings.filter(m => {
    const q = search.toLowerCase();
    return !q || [m.mapping_id, m.client_name, m.installer_name, m.package_ref, m.package_name, m.ref_number]
      .some(v => v?.toLowerCase().includes(q));
  });

  const counts = {
    total:     mappings.length,
    active:    mappings.filter(m => m.status === 'active').length,
    completed: mappings.filter(m => m.status === 'completed').length,
  };

  return (
    <Layout title="Product Mapping">

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border: `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Mappings', val: counts.total,     dot: C.primary },
          { label: 'Active',         val: counts.active,    dot: C.success },
          { label: 'Completed',      val: counts.completed, dot: C.info    },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        {tab === 'mappings' && <button onClick={openCreate} style={{ ...btnPrimary, marginLeft: 'auto' }}>+ Add Mapping</button>}
      </div>

      {/* ── Tabs: Mappings | Requests ── */}
      <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px', marginBottom: '18px', width: 'fit-content' }}>
        {[
          { k: 'mappings', l: '🗺️ Mappings', n: mappings.length },
          { k: 'requests', l: '📨 Requests',  n: reqCounts.pending, highlight: true },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            fontSize: '13px', fontWeight: 700,
            background: tab === t.k ? C.white : 'transparent',
            color: tab === t.k ? C.textMain : C.textSub,
            boxShadow: tab === t.k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
          }}>
            {t.l}
            {t.n > 0 && (
              <span style={{
                marginLeft: '7px', padding: '1px 7px', borderRadius: '20px', fontSize: '10px', fontWeight: 800,
                background: t.highlight ? '#FEF3C7' : '#E5E7EB',
                color:      t.highlight ? '#92400E' : C.textSub,
              }}>{t.n}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══ REQUESTS TAB ══ */}
      {tab === 'requests' && (
        <div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {['pending', 'approved', 'rejected'].map(k => (
              <button key={k} onClick={() => setReqTab(k)} style={{
                padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                border: `1.5px solid ${reqTab === k ? C.primary : C.border}`,
                background: reqTab === k ? C.primary : C.white,
                color: reqTab === k ? '#fff' : C.textSub,
              }}>
                {k.charAt(0).toUpperCase() + k.slice(1)}{reqCounts[k] > 0 ? ` (${reqCounts[k]})` : ''}
              </button>
            ))}
          </div>

          {requests.filter(r => r.status === reqTab).length === 0 ? (
            <div style={{ ...card, padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📨</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>No {reqTab} requests</div>
              <div style={{ fontSize: '12px', color: C.textSub }}>
                When an installer maps a package in their hand to a client, it arrives here for you to assign the cost and confirm.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
              {requests.filter(r => r.status === reqTab).map(r => (
                <div key={r.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                  <div style={{ padding: '13px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: C.primaryDeep, background: C.primaryBg, padding: '2px 9px', borderRadius: '6px' }}>{r.request_id}</span>
                      <span style={{ fontSize: '14px', fontWeight: 800, color: C.textMain }}>{r.client_name}</span>
                      <span style={{ fontSize: '11px', color: C.textSub }}>{r.client_ref}{r.client_contact ? ` · ${r.client_contact}` : ''}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: C.textSub }}>👷 {r.installer_name} · {r.created_at}</span>
                  </div>

                  <div style={{ padding: '12px 18px', display: 'flex', gap: '22px', flexWrap: 'wrap', fontSize: '12px' }}>
                    <span><span style={{ color: C.textSub }}>Package:</span> <strong style={{ fontFamily: 'monospace' }}>{r.package_ref}</strong></span>
                    {r.application_label && <span><span style={{ color: C.textSub }}>Vehicle:</span> <strong>{r.application_label}{r.application_reg ? ` · ${r.application_reg}` : ''}</strong></span>}
                    {r.ref_number && <span><span style={{ color: C.textSub }}>Ref:</span> <strong>{r.ref_number}</strong></span>}
                  </div>

                  <div style={{ padding: '0 18px 12px' }}>
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: '9px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' }}>
                        <thead><tr style={{ background: C.primaryBg }}>
                          {['Device', 'Serial / Ref', 'Category', 'List Cost', 'List AMC'].map(h => (
                            <th key={h} style={{ padding: '6px 11px', textAlign: 'left', fontSize: '9.5px', fontWeight: 800, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {r.items.map((it, i) => (
                            <tr key={it.inv_db_id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 ? '#FAFAFA' : C.white }}>
                              <td style={{ padding: '6px 11px', fontFamily: 'monospace', fontWeight: 700 }}>{it.item_id}</td>
                              <td style={{ padding: '6px 11px', fontFamily: 'monospace', color: C.textSub }}>{it.serial_number || '—'}{it.ref_number ? ` · ${it.ref_number}` : ''}</td>
                              <td style={{ padding: '6px 11px' }}>{it.category_name}{it.type_name ? ` · ${it.type_name}` : ''}</td>
                              <td style={{ padding: '6px 11px', color: C.primaryDeep, fontWeight: 700 }}>₹{(it.suggested_amount || 0).toLocaleString('en-IN')}</td>
                              <td style={{ padding: '6px 11px', color: '#7C3AED', fontWeight: 700 }}>{it.suggested_amc > 0 ? `₹${it.suggested_amc.toLocaleString('en-IN')}` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {r.notes && <div style={{ padding: '0 18px 10px', fontSize: '11.5px', color: C.textSub, fontStyle: 'italic' }}>Installer: “{r.notes}”</div>}

                  {r.status === 'pending' ? (
                    <div style={{ padding: '11px 18px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA', display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
                      <button onClick={() => openAct(r, 'reject')} style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', cursor: 'pointer' }}>✕ Reject</button>
                      <button onClick={() => openAct(r, 'approve')} style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', cursor: 'pointer' }}>💰 Assign Cost & Confirm</button>
                    </div>
                  ) : (
                    <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA', fontSize: '12px', color: r.status === 'approved' ? '#065F46' : '#991B1B' }}>
                      {r.status === 'approved'
                        ? <>✓ Confirmed as <strong>{r.mapping_ref}</strong> · ₹{(r.total_amount || 0).toLocaleString('en-IN')}{r.total_amc > 0 && ` + AMC ₹${r.total_amc.toLocaleString('en-IN')}`} · {r.decided_at}</>
                        : <>✕ Rejected {r.decided_at}{r.admin_notes ? ` — ${r.admin_notes}` : ''}</>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {tab === 'mappings' && (
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', alignItems: 'center' }}>
          <input placeholder="Search by ID, client, installer, package, ref…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, ...inputBase }} />
          {search && <button onClick={() => setSearch('')} style={btnGhost}>Clear</button>}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {mappings.length}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Mapping ID', 'Client', 'Installer', 'Package', 'Application', 'Amount', 'AMC', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} style={emptyCell}>
                  {mappings.length === 0 ? 'No mappings yet. Click "+ Add Mapping" to start.' : 'No results match your search.'}
                </td></tr>
              ) : filtered.map((m, i) => {
                const ss = STATUS_STYLE[m.status] || STATUS_STYLE.active;
                return (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C.primaryDeep }}>{m.mapping_id}</span>
                      {m.ref_number && <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>🔢 {m.ref_number}</div>}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{m.client_name}</div>
                      <div style={{ fontSize: '11px', color: C.textSub }}>{m.client_ref}</div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{m.installer_name}</div>
                      <div style={{ fontSize: '11px', color: C.textSub }}>@{m.installer_user}</div>
                    </td>
                    <td style={td}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#8B5CF6' }}>{m.package_ref}</div>
                      {m.package_name && <div style={{ fontSize: '11px', color: C.textSub }}>{m.package_name}</div>}
                    </td>
                    <td style={td}>
                      {m.application_name
                        ? <><div style={{ fontSize: '12px', fontWeight: 600, color: '#0369A1' }}>{m.application_name}</div>
                            <div style={{ fontSize: '10px', color: C.textSub, fontFamily: 'monospace' }}>{m.application_ref}</div></>
                        : <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>}
                    </td>
                    <td style={td}><span style={{ fontWeight: 700, color: C.textMain }}>₹{(m.total_amount || 0).toLocaleString()}</span></td>
                    <td style={td}><span style={{ fontWeight: 700, color: '#8B5CF6' }}>₹{(m.total_amc || 0).toLocaleString()}</span></td>
                    <td style={td}><span style={{ background: ss.bg, color: ss.color, padding: '3px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{ss.label}</span></td>
                    <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{m.created_at}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <SmBtn label="Edit"   color={C.primary}  onClick={() => openEdit(m)} />
                        <SmBtn label="Delete" color={C.danger}   onClick={() => { setSelMap(m); setModal('delete'); }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ── Create Modal ── */}
      {modal === 'create' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '660px' }}>
            <MHeader title="Add Product Mapping" onClose={() => setModal(null)} accent={C.primary} />
            <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: '80vh' }}>
              {fErr && <ErrBox msg={fErr} />}

              {/* ── Client searchable combobox ── */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelS}>Client *</label>
                <div ref={clientDropRef} style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      value={clientSearch}
                      onChange={e => {
                        setClientSearch(e.target.value);
                        setClientDropOpen(true);
                        if (!e.target.value) setForm(f => ({ ...f, client_id: '' }));
                      }}
                      onFocus={() => setClientDropOpen(true)}
                      placeholder="Search client by name or ID…"
                      style={{
                        ...inputBase,
                        paddingRight: '32px',
                        borderColor: form.client_id ? C.primary : C.border,
                      }}
                    />
                    {form.client_id
                      ? <span
                          onClick={() => { setForm(f => ({ ...f, client_id: '' })); setClientSearch(''); setClientDropOpen(true); }}
                          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: C.textSub, fontSize: '14px', lineHeight: 1 }}
                        >✕</span>
                      : <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textSub, fontSize: '12px', pointerEvents: 'none' }}>▾</span>
                    }
                  </div>

                  {clientDropOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
                      background: C.white, border: `1.5px solid ${C.primary}`,
                      borderTop: 'none', borderRadius: '0 0 10px 10px',
                      maxHeight: '200px', overflowY: 'auto',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }}>
                      {filteredClients.length === 0
                        ? <div style={{ padding: '12px 14px', fontSize: '12px', color: C.textSub }}>No clients found.</div>
                        : filteredClients.map(c => (
                            <div
                              key={c.id}
                              onMouseDown={() => selectClient(c)}
                              style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: '13px',
                                borderBottom: `1px solid ${C.border}`,
                                background: form.client_id === c.id ? '#FFF7ED' : C.white,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#FFF7ED'}
                              onMouseLeave={e => e.currentTarget.style.background = form.client_id === c.id ? '#FFF7ED' : C.white}
                            >
                              <div>
                                <span style={{ fontWeight: 600, color: C.textMain }}>{c.client_name}</span>
                                {c.company_name && <span style={{ fontSize: '11px', color: C.textSub, marginLeft: '6px' }}>{c.company_name}</span>}
                              </div>
                              {c.client_id && <span style={{ fontSize: '11px', color: C.textSub, fontFamily: 'monospace' }}>{c.client_id}</span>}
                            </div>
                          ))
                      }
                    </div>
                  )}
                </div>

                {/* Selected client info card */}
                {selectedClientObj && (
                  <div style={{
                    marginTop: '10px', padding: '12px 14px',
                    background: '#F0F9FF', border: '1.5px solid #BAE6FD',
                    borderRadius: '10px', display: 'flex', gap: '18px', flexWrap: 'wrap',
                  }}>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', marginBottom: '2px' }}>Client</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{selectedClientObj.client_name}</div>
                      {selectedClientObj.client_id && <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#0369A1' }}>{selectedClientObj.client_id}</div>}
                    </div>
                    {selectedClientObj.company_name && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', marginBottom: '2px' }}>Company</div>
                        <div style={{ fontSize: '13px', color: C.textMain }}>{selectedClientObj.company_name}</div>
                      </div>
                    )}
                    {selectedClientObj.contact && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', marginBottom: '2px' }}>Contact</div>
                        <div style={{ fontSize: '13px', color: C.textMain }}>{selectedClientObj.contact}</div>
                      </div>
                    )}
                    {selectedClientObj.email && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', marginBottom: '2px' }}>Email</div>
                        <div style={{ fontSize: '13px', color: C.textMain }}>{selectedClientObj.email}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Installer */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelS}>Installer *</label>
                <select value={form.installer_id} onChange={e => onInstallerChange(e.target.value)} style={selS}>
                  <option value="">— Select Installer —</option>
                  {installers.map(u => <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>)}
                </select>
              </div>

              {/* Application + Application No */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelS}>Application</label>
                  <select
                    value={form.application_id}
                    onChange={e => setForm(f => ({ ...f, application_id: e.target.value }))}
                    style={selS}
                  >
                    <option value="">— Select Application —</option>
                    {applications.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.app_id ? ` (${a.app_id})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelS}>Application No</label>
                  <input
                    value={form.ref_number}
                    onChange={e => setForm(f => ({ ...f, ref_number: e.target.value }))}
                    placeholder="Enter Application No"
                    style={{
                      ...inputBase,
                      borderColor: form.ref_number.trim() ? C.primary : C.border,
                    }}
                  />
                </div>
              </div>

              {/* Package select */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelS}>Package (currently with installer) *</label>
                {loadingPkgs ? (
                  <div style={{ padding: '10px', fontSize: '12px', color: C.textSub }}>Loading packages…</div>
                ) : (
                  <select
                    value={form.package_id}
                    onChange={e => onPackageChange(e.target.value)}
                    disabled={!form.installer_id}
                    style={{ ...selS, opacity: form.installer_id ? 1 : 0.5 }}
                  >
                    <option value="">— Select Package —</option>
                    {instPkgs.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.package_id}{p.name ? ` — ${p.name}` : ''} ({p.item_count} item{p.item_count !== 1 ? 's' : ''})
                      </option>
                    ))}
                  </select>
                )}
                {form.installer_id && !loadingPkgs && instPkgs.length === 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 13px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
                    ⚠ No available packages for this installer. Packages already mapped to a client are not shown here.
                  </div>
                )}
              </div>

              {/* Package item breakdown */}
              {loadingSummary && (
                <div style={{ padding: '12px', fontSize: '12px', color: C.textSub, textAlign: 'center' }}>Fetching price summary…</div>
              )}
              {pkgItems.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Package Price Breakdown</div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: C.primaryBg }}>
                          {['Item', 'Category', 'Type', 'Price (₹)', 'AMC (₹)'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 700, color: C.textSub, textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pkgItems.map((it, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : '#FFFBF5' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>{it.item_id}</div>
                              {it.serial_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '2px' }}>SN: {it.serial_number}</div>}
                              {it.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {it.ref_number}</div>}
                            </td>
                            <td style={{ padding: '8px 12px', fontSize: '12px' }}>{it.category}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: C.textSub }}>{it.type || '—'}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>₹{(it.price || 0).toLocaleString()}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 700, color: '#6D28D9' }}>₹{(it.amc || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Amount + AMC inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelS}>Total Amount (₹)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: C.textSub, fontWeight: 600 }}>₹</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={form.total_amount}
                      onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))}
                      placeholder="0.00"
                      style={{ ...inputBase, paddingLeft: '26px', fontWeight: 700 }}
                    />
                  </div>
                  <div style={{ fontSize: '10px', color: C.textSub, marginTop: '3px' }}>Auto-fetched — editable</div>
                </div>
                <div>
                  <label style={labelS}>Total AMC (₹)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: C.textSub, fontWeight: 600 }}>₹</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={form.total_amc}
                      onChange={e => setForm(f => ({ ...f, total_amc: e.target.value }))}
                      placeholder="0.00"
                      style={{ ...inputBase, paddingLeft: '26px', fontWeight: 700, color: '#6D28D9' }}
                    />
                  </div>
                  <div style={{ fontSize: '10px', color: C.textSub, marginTop: '3px' }}>Auto-fetched — editable</div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={labelS}>Notes (optional)</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} placeholder="Any additional notes…"
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>

            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={saveMapping} disabled={saving} style={btnPrimary}>
                {saving ? 'Saving…' : 'Save Mapping'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Edit Modal ── */}
      {modal === 'edit' && selMap && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '480px' }}>
            <MHeader title={`Edit — ${selMap.mapping_id}`} onClose={() => setModal(null)} accent={C.primary} />
            <div style={{ padding: '22px 28px' }}>
              {fErr && <ErrBox msg={fErr} />}

              {/* Read-only context */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
                <InfoChip label="Client"    val={selMap.client_name}    />
                <InfoChip label="Installer" val={selMap.installer_name} />
                <InfoChip label="Package"   val={selMap.package_ref}    mono />
                {selMap.application_name && <InfoChip label="App" val={selMap.application_name} />}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelS}>Total Amount (₹)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: C.textSub, fontWeight: 600 }}>₹</span>
                    <input type="number" min="0" step="0.01"
                      value={selMap.total_amount}
                      onChange={e => setSelMap(s => ({ ...s, total_amount: e.target.value }))}
                      style={{ ...inputBase, paddingLeft: '26px', fontWeight: 700 }} />
                  </div>
                </div>
                <div>
                  <label style={labelS}>Total AMC (₹)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: C.textSub, fontWeight: 600 }}>₹</span>
                    <input type="number" min="0" step="0.01"
                      value={selMap.total_amc}
                      onChange={e => setSelMap(s => ({ ...s, total_amc: e.target.value }))}
                      style={{ ...inputBase, paddingLeft: '26px', fontWeight: 700, color: '#6D28D9' }} />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelS}>Application No</label>
                <input
                  value={selMap.ref_number || ''}
                  onChange={e => setSelMap(s => ({ ...s, ref_number: e.target.value }))}
                  placeholder="Enter Application No"
                  style={{ ...inputBase, borderColor: selMap.ref_number?.trim() ? C.primary : C.border }}
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelS}>Status</label>
                <select value={selMap.status} onChange={e => setSelMap(s => ({ ...s, status: e.target.value }))} style={selS}>
                  {Object.entries(STATUS_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <div>
                <label style={labelS}>Notes</label>
                <textarea value={selMap.notes || ''} onChange={e => setSelMap(s => ({ ...s, notes: e.target.value }))}
                  rows={2} placeholder="Notes…"
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={saveEdit} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Update'}</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && selMap && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Mapping" onClose={() => setModal(null)} accent={C.danger} />
            <div style={{ padding: '20px 28px' }}>
              <div style={{ background: C.warningBg, border: `1px solid ${C.warning}30`, borderRadius: '10px', padding: '14px', fontSize: '13px', color: C.warningText }}>
                ⚠ Delete <strong style={{ fontFamily: 'monospace' }}>{selMap.mapping_id}</strong>? This cannot be undone.
              </div>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={confirmDelete} disabled={saving} style={{ ...btnPrimary, background: C.danger, boxShadow: 'none' }}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Assign cost / Reject request modal ── */}
      {act && (() => {
        const isApprove = act.mode === 'approve';
        return (
          <Overlay>
            <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '430px' }}>
              <div style={{ padding: '17px 22px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '15px', fontWeight: 800, color: C.textMain }}>
                  {isApprove ? '💰 Assign Cost & Confirm' : '✕ Reject Request'}
                </div>
                <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                  {act.req.request_id} · {act.req.client_name} · {act.req.package_ref}
                </div>
              </div>
              <div style={{ padding: '18px 22px' }}>
                {isApprove ? (
                  <>
                    <div style={{ marginBottom: '13px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>Product cost (₹) *</label>
                      <input type="number" min="0" value={reqAmount} autoFocus
                        onChange={e => { setReqAmount(e.target.value); setReqErr(''); }}
                        style={{ width: '100%', padding: '10px 13px', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '16px', fontWeight: 700, outline: 'none' }} />
                    </div>
                    <div style={{ marginBottom: '13px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>AMC cost (₹) <span style={{ color: C.textSub, fontWeight: 500 }}>· charged at renewal</span></label>
                      <input type="number" min="0" value={reqAmc}
                        onChange={e => setReqAmc(e.target.value)}
                        style={{ width: '100%', padding: '10px 13px', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '16px', fontWeight: 700, outline: 'none' }} />
                    </div>
                    <div style={{ background: C.primaryBg, borderRadius: '9px', padding: '10px 13px', fontSize: '12px', color: C.primaryDeep, marginBottom: '12px' }}>
                      Total to collect now: <strong style={{ fontSize: '15px' }}>₹{(Number(reqAmount) || 0).toLocaleString('en-IN')}</strong>
                      <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>
                        Only the product cost is collected at mapping. AMC (₹{(Number(reqAmc) || 0).toLocaleString('en-IN')}) is charged separately at renewal.
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '9px', padding: '10px 13px', fontSize: '12px', color: '#92400E', marginBottom: '12px', lineHeight: 1.5 }}>
                    The devices stay in the installer's hands and can be mapped again.
                  </div>
                )}
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>
                  Note to installer {isApprove ? '(optional)' : '(reason)'}
                </label>
                <textarea value={reqNote} onChange={e => setReqNote(e.target.value)} rows={2}
                  style={{ width: '100%', padding: '9px 12px', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                {reqErr && <div style={{ marginTop: '9px', color: C.danger, fontSize: '12px', fontWeight: 600 }}>{reqErr}</div>}
              </div>
              <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
                <button onClick={() => setAct(null)} disabled={reqBusy} style={btnGhost}>Cancel</button>
                <button onClick={submitAct} disabled={reqBusy} style={{
                  padding: '9px 22px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: reqBusy ? 'not-allowed' : 'pointer', color: '#fff',
                  background: reqBusy ? '#9CA3AF' : isApprove ? `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})` : '#EF4444',
                }}>{reqBusy ? 'Working…' : isApprove ? '✓ Confirm Mapping' : '✕ Reject'}</button>
              </div>
            </div>
          </Overlay>
        );
      })()}
    </Layout>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      {children}
    </div>
  );
}

function MHeader({ title, onClose, accent = C.primary }) {
  return (
    <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: accent, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function ErrBox({ msg }) {
  return (
    <div style={{ background: C.dangerBg, color: C.dangerText, border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
      ⚠ {msg}
    </div>
  );
}

function InfoChip({ label, val, mono }) {
  return (
    <div style={{ background: C.primaryBg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '6px 12px' }}>
      <div style={{ fontSize: '10px', color: C.textSub, fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain, fontFamily: mono ? 'monospace' : 'inherit' }}>{val || '—'}</div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inputBase  = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box', background: C.white };
const card       = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox   = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th         = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td         = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell  = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const labelS     = { display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' };
const selS       = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer', boxSizing: 'border-box' };
const btnPrimary = { padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)` };
const btnGhost   = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
